import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { getEdgeFunctionUrl } from './edge-function-url';
import type { BrandVoiceSettings, SamplePost, PlatformGuideline } from '@shared/types/database';
import { toast } from 'react-hot-toast';

// =====================================================
// Brand Voice Settings - AI generation guidelines
// =====================================================

export type EmojiUsage = 'none' | 'minimal' | 'moderate' | 'frequent';

export interface BrandVoiceFormData {
  tone?: string[];
  keywords?: string[];
  words_to_avoid?: string[];
  target_audience?: string;
  sample_posts?: SamplePost[];
  platform_guidelines?: Record<string, PlatformGuideline>;
  default_hashtags?: string[];
  emoji_usage?: EmojiUsage;
  call_to_action_style?: string;
  language?: string; // 'en-US', 'de-DE', etc.
}

/**
 * Fetch brand voice settings for a brand
 */
export function useBrandVoice(brandId: string | undefined) {
  return useQuery({
    queryKey: ['brand-voice', brandId],
    queryFn: async (): Promise<BrandVoiceSettings | null> => {
      if (!brandId) return null;

      const { data, error } = await supabase
        .from('brand_voice_settings')
        .select('*')
        .eq('brand_id', brandId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        console.error('[useBrandVoice] Error:', error);
        throw error;
      }

      return data;
    },
    enabled: !!brandId,
  });
}

/**
 * Create or update brand voice settings
 * Uses upsert to handle both create and update
 */
export function useUpdateBrandVoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      brandId,
      settings,
    }: {
      brandId: string;
      settings: BrandVoiceFormData;
    }) => {
      const { data, error } = await supabase
        .from('brand_voice_settings')
        .upsert(
          {
            brand_id: brandId,
            tone: settings.tone || [],
            keywords: settings.keywords || [],
            words_to_avoid: settings.words_to_avoid || [],
            target_audience: settings.target_audience || null,
            sample_posts: settings.sample_posts || [],
            platform_guidelines: settings.platform_guidelines || {},
            default_hashtags: settings.default_hashtags || [],
            emoji_usage: settings.emoji_usage || 'moderate',
            call_to_action_style: settings.call_to_action_style || null,
            language: settings.language || 'en-US',
          },
          {
            onConflict: 'brand_id',
          }
        )
        .select()
        .single();

      if (error) {
        console.error('[useUpdateBrandVoice] Error:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-voice', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['brand', data.brand_id] });
      toast.success('Brand voice settings saved');
    },
    onError: (error) => {
      toast.error(`Failed to save brand voice: ${error.message}`);
    },
  });
}

/**
 * Delete brand voice settings
 */
export function useDeleteBrandVoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brandId: string) => {
      const { error } = await supabase
        .from('brand_voice_settings')
        .delete()
        .eq('brand_id', brandId);

      if (error) {
        console.error('[useDeleteBrandVoice] Error:', error);
        throw error;
      }

      return brandId;
    },
    onSuccess: (brandId) => {
      queryClient.invalidateQueries({ queryKey: ['brand-voice', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand', brandId] });
      toast.success('Brand voice settings deleted');
    },
    onError: (error) => {
      toast.error(`Failed to delete brand voice: ${error.message}`);
    },
  });
}

// =====================================================
// AI Draft Generation - Gemini 3.0
// =====================================================

export interface GenerateDraftRequest {
  eventName: string;
  eventDate: string;
  eventType: string;
  platforms: string[]; // e.g., ['linkedin', 'twitter', 'instagram']
  brandId?: string;
  voiceId?: string; // Brand voice ID for multi-voice selection
  additionalContext?: string;
  refinement?: {
    type: 'shorter' | 'longer' | 'funnier' | 'formal';
    currentContent: string;
    currentHashtags?: string[];
  };
}

export interface GeneratedDraft {
  content: string;
  hashtags: string[];
  suggestedPlatforms: string[];
  suggestedTime?: string; // ISO datetime
  characterCount: number;
  warnings?: string[]; // e.g., "Content exceeds Twitter limit"
}

/**
 * Generate social media draft using AI
 * Calls the generate-social-draft Edge Function (Gemini 3.0)
 */
export function useGenerateDraft() {
  return useMutation({
    mutationFn: async (request: GenerateDraftRequest): Promise<GeneratedDraft> => {
      const { data: { session }, error: authError } = await supabase.auth.refreshSession();
      if (authError || !session) throw new Error('Not authenticated');

      const url = getEdgeFunctionUrl('generate-social-draft');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(request),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data as GeneratedDraft;
    },
    onError: (error) => {
      toast.error(`Failed to generate draft: ${error.message}`);
    },
  });
}

// =====================================================
// Tone and Voice Helpers
// =====================================================

export const TONE_GROUPS = [
  {
    labelKey: 'brands.toneGroups.style',
    tones: [
      { value: 'professional', labelKey: 'brands.tones.professional' },
      { value: 'casual', labelKey: 'brands.tones.casual' },
      { value: 'formal', labelKey: 'brands.tones.formal' },
      { value: 'conversational', labelKey: 'brands.tones.conversational' },
      { value: 'direct', labelKey: 'brands.tones.direct' },
      { value: 'clear', labelKey: 'brands.tones.clear' },
    ],
  },
  {
    labelKey: 'brands.toneGroups.authorityTrust',
    tones: [
      { value: 'confident', labelKey: 'brands.tones.confident' },
      { value: 'authoritative', labelKey: 'brands.tones.authoritative' },
      { value: 'trustworthy', labelKey: 'brands.tones.trustworthy' },
      { value: 'persuasive', labelKey: 'brands.tones.persuasive' },
      { value: 'results-oriented', labelKey: 'brands.tones.resultsOriented' },
      { value: 'value-focused', labelKey: 'brands.tones.valueFocused' },
    ],
  },
  {
    labelKey: 'brands.toneGroups.warmthEmpathy',
    tones: [
      { value: 'friendly', labelKey: 'brands.tones.friendly' },
      { value: 'empathetic', labelKey: 'brands.tones.empathetic' },
      { value: 'patient', labelKey: 'brands.tones.patient' },
      { value: 'reassuring', labelKey: 'brands.tones.reassuring' },
      { value: 'helpful', labelKey: 'brands.tones.helpful' },
      { value: 'understanding', labelKey: 'brands.tones.understanding' },
      { value: 'approachable', labelKey: 'brands.tones.approachable' },
    ],
  },
  {
    labelKey: 'brands.toneGroups.energyCreativity',
    tones: [
      { value: 'enthusiastic', labelKey: 'brands.tones.enthusiastic' },
      { value: 'bold', labelKey: 'brands.tones.bold' },
      { value: 'creative', labelKey: 'brands.tones.creative' },
      { value: 'playful', labelKey: 'brands.tones.playful' },
      { value: 'witty', labelKey: 'brands.tones.witty' },
      { value: 'engaging', labelKey: 'brands.tones.engaging' },
      { value: 'entertaining', labelKey: 'brands.tones.entertaining' },
      { value: 'inspirational', labelKey: 'brands.tones.inspirational' },
      { value: 'trendy', labelKey: 'brands.tones.trendy' },
    ],
  },
  {
    labelKey: 'brands.toneGroups.purposeConnection',
    tones: [
      { value: 'authentic', labelKey: 'brands.tones.authentic' },
      { value: 'relatable', labelKey: 'brands.tones.relatable' },
      { value: 'educational', labelKey: 'brands.tones.educational' },
      { value: 'proactive', labelKey: 'brands.tones.proactive' },
      { value: 'solution-focused', labelKey: 'brands.tones.solutionFocused' },
      { value: 'urgent', labelKey: 'brands.tones.urgent' },
    ],
  },
] as const;

/** Flat list of all tone options (derived from groups) */
export const TONE_OPTIONS = TONE_GROUPS.flatMap((g) => g.tones);

export const EMOJI_USAGE_OPTIONS = [
  { value: 'none', labelKey: 'brands.emojiUsage.none', descriptionKey: 'brands.emojiUsage.noneDesc' },
  { value: 'minimal', labelKey: 'brands.emojiUsage.minimal', descriptionKey: 'brands.emojiUsage.minimalDesc' },
  { value: 'moderate', labelKey: 'brands.emojiUsage.moderate', descriptionKey: 'brands.emojiUsage.moderateDesc' },
  { value: 'frequent', labelKey: 'brands.emojiUsage.frequent', descriptionKey: 'brands.emojiUsage.frequentDesc' },
] as const;

export const PLATFORM_CONFIGS = {
  linkedin: {
    name: 'LinkedIn',
    maxLength: 3000,
    hashtagLimit: 5,
    bestTimes: ['09:00', '12:00', '17:00'],
    toneHint: 'Professional, industry-focused',
  },
  twitter: {
    name: 'Twitter/X',
    maxLength: 280,
    hashtagLimit: 3,
    bestTimes: ['09:00', '12:00', '15:00', '18:00'],
    toneHint: 'Concise, engaging, conversational',
  },
  instagram: {
    name: 'Instagram',
    maxLength: 2200,
    hashtagLimit: 30,
    bestTimes: ['11:00', '13:00', '19:00'],
    toneHint: 'Visual-focused, lifestyle-oriented',
  },
  facebook: {
    name: 'Facebook',
    maxLength: 63206,
    hashtagLimit: 3,
    bestTimes: ['09:00', '13:00', '16:00'],
    toneHint: 'Friendly, community-focused',
  },
  tiktok: {
    name: 'TikTok',
    maxLength: 2200,
    hashtagLimit: 5,
    bestTimes: ['19:00', '21:00'],
    toneHint: 'Trendy, entertaining, authentic',
  },
  threads: {
    name: 'Threads',
    maxLength: 500,
    hashtagLimit: 5,
    bestTimes: ['10:00', '14:00', '18:00'],
    toneHint: 'Conversational, authentic, community-driven',
  },
  bluesky: {
    name: 'Bluesky',
    maxLength: 300,
    hashtagLimit: 5,
    bestTimes: ['09:00', '12:00', '17:00'],
    toneHint: 'Authentic, decentralized-friendly, conversational',
  },
} as const;

export type SupportedPlatform = keyof typeof PLATFORM_CONFIGS;

/**
 * Check if content exceeds platform limits
 */
export function checkPlatformLimits(
  content: string,
  hashtags: string[],
  platforms: SupportedPlatform[]
): { platform: string; warning: string }[] {
  const warnings: { platform: string; warning: string }[] = [];

  for (const platform of platforms) {
    const config = PLATFORM_CONFIGS[platform];
    if (!config) continue;

    // Check content length
    if (content.length > config.maxLength) {
      warnings.push({
        platform: config.name,
        warning: `Content exceeds ${config.name} limit (${content.length}/${config.maxLength} chars)`,
      });
    }

    // Check hashtag count
    if (hashtags.length > config.hashtagLimit) {
      warnings.push({
        platform: config.name,
        warning: `Too many hashtags for ${config.name} (${hashtags.length}/${config.hashtagLimit})`,
      });
    }
  }

  return warnings;
}
